import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { toast, Toaster as Sonner, type ToasterProps } from 'sonner'

import { useColorScheme } from '#ui/lib/use-color-scheme.ts'

/**
 * A style object that may also carry custom properties.
 *
 * React's `CSSProperties` enumerates the known CSS properties and nothing else, so `--normal-bg`
 * is a type error — which is why the generated component asserts the whole object with
 * `as React.CSSProperties`. That assertion narrows, and the type-aware lint pass refuses it for
 * good reason: it would also silence a genuine typo in a real property name.
 *
 * Widening the type instead keeps every ordinary property checked and allows exactly the thing
 * that needs allowing.
 */
type StyleWithCustomProperties = React.CSSProperties & Record<`--${string}`, string>

/** Sonner's own surface tokens, pointed at the tenant's. */
const TOAST_TOKENS: StyleWithCustomProperties = {
  '--normal-bg': 'var(--popover)',
  '--normal-text': 'var(--popover-foreground)',
  '--normal-border': 'var(--border)',
  '--border-radius': 'var(--radius)',
}

/**
 * Toasts.
 *
 * **`next-themes` has been removed**, which is the one edit to what the registry generates. The
 * upstream component reads the mode from `useTheme()`, and installing it here would put a second
 * source of truth for light/dark beside the one `packages/theme` already owns — the class on
 * `<html>` that the boot shim sets before first paint. Two of them disagree for at least one render
 * on every cold load, and the disagreement is invisible until somebody is looking at a white toast
 * on a black page. `useColorScheme` reads that same class, so there is only ever one answer.
 *
 * The colors come from the tenant's tokens rather than from sonner's own palette, so a toast is
 * the event's brand rather than the library's. `--border-radius` is the tenant's radius for the same
 * reason every other corner in the product now is.
 */
function Toaster({ ...props }: ToasterProps) {
  const { scheme } = useColorScheme()

  return (
    <Sonner
      theme={scheme}
      className="toaster group"
      icons={{
        // Every status carries a shape as well as a color. A toast that says "paid" in green and
        // "refused" in red, and nothing else, is a toast that says the same thing twice to roughly
        // one reader in twelve.
        //
        // The colors are the platform's status tokens (`--success`, `--warning`, `--info` in
        // `base.css`), not Tailwind's palette: they have a light and a dark value, and a tenant
        // cannot change them, so an error is red in every edition and in both modes.
        success: <CircleCheckIcon className="text-success size-4 shrink-0" />,
        info: <InfoIcon className="text-info size-4 shrink-0" />,
        warning: <TriangleAlertIcon className="text-warning size-4 shrink-0" />,
        error: <OctagonXIcon className="text-destructive size-4 shrink-0" />,
        loading: <Loader2Icon className="size-4 shrink-0 animate-spin" />,
      }}
      // A title over a smaller, quieter description: the title says what happened, and the
      // description says why or what to do. In one size and one color the two read as one line.
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-popover group-[.toaster]:text-popover-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg font-sans text-sm',
          title: 'font-medium text-sm',
          description: 'text-muted-foreground text-xs leading-normal',
          actionButton: 'bg-primary text-primary-foreground text-xs font-medium',
          cancelButton: 'bg-muted text-muted-foreground text-xs font-medium',
        },
      }}
      style={TOAST_TOKENS}
      {...props}
    />
  )
}

/**
 * `toast` is exported from **here**, beside the `Toaster` that renders it, and apps must import it
 * from here rather than from `sonner`.
 *
 * Sonner keeps its queue in module state. The console once depended on `sonner@2.0.7` while this
 * package depended on `2.0.8`, so the two resolved to two copies: every `toast()` the console called
 * went into one copy's queue while this `Toaster` rendered the other's. No toast ever appeared, and
 * nothing errored — a refused role change simply looked like nothing had happened. Importing both
 * halves from one module makes them one instance by construction; `no-restricted-imports` holds it.
 */
export { Toaster, toast }
